/**
 * #2586 — what a date control is holding that its stored value cannot say.
 *
 * `MemorableDateInput` stores `YYYY-MM-DD` or null. A half-typed date (month
 * and day picked, year empty) stores null, exactly like an untouched one, so
 * a step validator reading stored values alone cannot tell "the family has
 * not started" from "the family is halfway through" — and Save on a required
 * half-typed date said "This question is required." while Save on an
 * optional one advanced and dropped the parts silently.
 *
 * This is the side channel: each date control reports, by field key, the
 * message it would show for its current draft (null when the draft is empty
 * or a real date), already in the reader's language. The surface that owns
 * step validation (the application wizard) provides the context and reads
 * the map when the family presses Save. A surface that provides nothing gets
 * the stored-value behaviour it always had.
 */
import { createContext, useContext } from 'react';

export interface DateDraftRegistry {
  /** Record the control's current problem, or clear it (null). */
  report: (fieldKey: string, problem: string | null) => void;
  /** True while the surface shows its own marker for this field, so the
   *  control does not say the same sentence twice. */
  flagged: (fieldKey: string) => boolean;
}

export const DateDraftContext = createContext<DateDraftRegistry | null>(null);

export function useDateDraftRegistry(): DateDraftRegistry | null {
  return useContext(DateDraftContext);
}
